import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { canResumeMissionProgress } from '../../platform/missions';
import { INVENTED_READINESS_COPY } from '../../platform/config/camera-readiness';
import type { MissionProgressSnapshot } from '../../shared/types/mission-progress';
import { resolveSitting } from '../session/resolve-sitting';
import { sessionResumeIdentity } from '../session/session-players';
import { useAppServices } from '../shell/services-context';

/**
 * Home, with the one affordance Slice D owns: carrying on an interrupted mission.
 *
 * There is a single snapshot slot, so the child who saved it is not necessarily the child at the
 * screen. The offer is therefore gated by the same `canResumeMissionProgress` guard the stage
 * uses before it actually resumes — anything else would hand a sibling another child's score and
 * round position, and would record the rest of the mission's evidence against the wrong name.
 * The mission identity comes from the snapshot itself (this offer is about *that* saved mission);
 * what the guard decides here is whose it is.
 *
 * Starting fresh needs no confirmation: the first save of a new session overwrites the slot, and
 * a launch path for five-year-olds should not contain a question only an adult can answer.
 *
 * **Superseded (D-054(2), Slice R):** the Phase 2 accepted bound — "a cooperative mission is not
 * offered here until a grown-up has set up two heroes again", because the identity depended on a
 * launch config a refresh emptied — is gone. The sitting is now reconstructed from a governed
 * launch-identity record, through `resolveSitting`, which is the *same* helper the stage boots
 * from. That shared call is the point: an offer made on a differently built identity would either
 * withhold a child's own mission or hand them somebody else's (D-046(4)).
 *
 * **Accepted bound that remains:** the shell header still reads "No player chosen" after a reload
 * until somebody picks. The sitting is reconstructed for the mission and for the card below, which
 * names the restored child — not for the chrome.
 */
export function HomePage() {
  const { repositories, activePlayer, sessionLaunchConfig, audio } = useAppServices();
  const [resumable, setResumable] = useState<MissionProgressSnapshot | null>(null);
  /** Whose mission the card is offering back — the restored child's, when a reload rebuilt one. */
  const [resumableLabel, setResumableLabel] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const snapshot = await repositories.missionProgress.getProgress();
      if (snapshot === null) {
        // Still resolved, because a launch identity that outlived its mission is stale and is
        // cleared by the same helper rather than left for every future launch to re-examine.
        await resolveSitting(repositories, { sessionLaunchConfig, activePlayer });
        if (!cancelled) {
          setResumable(null);
          setResumableLabel(null);
        }
        return;
      }

      const sitting = await resolveSitting(repositories, {
        gameId: snapshot.gameId,
        sessionLaunchConfig,
        activePlayer,
      });
      const offer = canResumeMissionProgress(snapshot, {
        missionId: snapshot.missionId,
        players: sessionResumeIdentity(sitting.config, sitting.player),
      });
      if (!cancelled) {
        setResumable(offer ? snapshot : null);
        setResumableLabel(offer ? (sitting.player?.label ?? null) : null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repositories, activePlayer, sessionLaunchConfig]);

  return (
    <section>
      <h1>Thrive Games</h1>
      <p className="lede">Move. Think. Laugh. Grow.</p>
      <p className="lede">
        Learning games your family plays with their whole body — on one screen, in one room, with
        nothing leaving this device.
      </p>

      {resumable === null ? null : (
        <div className="panel" data-testid="continue-playing">
          <h2 style={{ marginTop: 0 }}>Still going</h2>
          <p className="lede">
            There is a mission waiting where {activePlayer?.label ?? resumableLabel ?? 'you'} left
            off.
          </p>
          <p>
            {/*
             * The resumed sitting's autoplay unlock, riding the activation that already exists
             * (R1-2; PS5-5's "unlock rides the parent's existing start gesture", D-096(2)'s "no new
             * gesture may be invented").
             *
             * A reload builds a new, locked audio service, and this card is the only route back
             * into an interrupted mission — it never passes the setup screen where Slice 0″ does
             * the unlocking. Without this, the whole resumed mission ran silent: no bed, no team
             * cue, and every baked line pushed onto the browser voice because the baked path checks
             * `unlocked` per line. Pressing "Carry on" is a real user activation, which is all any
             * browser's autoplay policy asks for; nothing new is shown, and nothing is played here.
             *
             * A direct reload straight onto the mission URL activates nothing and stays honestly
             * locked, on the documented per-line TTS fallback.
             */}
            <Link
              to={`/play/${resumable.gameId}`}
              className="button button--primary"
              onClick={() => {
                void audio.unlock();
              }}
            >
              Carry on
            </Link>
          </p>
        </div>
      )}

      <p>
        <Link to="/catalogue" className="button button--primary">
          Play
        </Link>{' '}
        {/* The camera readiness shortcut architecture §2 puts on the home screen (CAM-01). */}
        <Link to="/camera-check" className="button">
          {INVENTED_READINESS_COPY.homeShortcut}
        </Link>
      </p>
    </section>
  );
}
